import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import CTASection from "@/components/CTASection";
import { ArrowRight, Sparkles, Check } from "lucide-react";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Popular Austin Services" }];

const INTERIOR_FEATURE_BULLETS = [
  "Accent walls",
  "Electric fireplace feature walls",
  "TV centers and media walls",
  "Built-in shelving",
  "Custom trim and paneling",
  "Living room focal-point upgrades",
];

const SECTIONS = [
  { title: "Outdoor Living", items: [
    ["Covered Patios","/services/patio-covers"],
    ["Pergolas","/services/pergolas"],
    ["Decks","/services/deck-building"],
    ["Outdoor Kitchens","/services/outdoor-living"],
  ]},
  { title: "Flexible Living Space", items: [
    ["ADUs","/services/adu-construction"],
    ["Garage Conversions","/services/garage-conversions"],
    ["Home Offices","/services/home-additions"],
    ["Home Additions","/services/home-additions"],
  ]},
  { title: "Practical Upgrades", items: [
    ["Energy-Efficient Updates","/services/exterior-repairs"],
    ["Bathroom Refreshes","/services/bathroom-remodeling"],
    ["Kitchen Upgrades","/services/kitchen-remodeling"],
    ["Roof Upgrades","/services/roof-replacement"],
  ]},
  { title: "Exterior & Durability", items: [
    ["Storm-Resistant Improvements","/services/storm-damage-roofing"],
    ["Low-Maintenance Exterior","/services/siding-trim-repairs"],
    ["Smart-Home Ready Remodels","/services/full-home-remodeling"],
  ]},
  { title: "Aging & Investment", items: [
    ["Aging-in-Place Modifications","/services/bathroom-remodeling"],
    ["Investor / Rental Repairs","/services/investor-property-repairs"],
    ["Make-Ready Improvements","/services/make-ready-repairs"],
  ]},
];

function InteriorFeatureUpgradesSection() {
  return (
    <section className="section-pad bg-white border-y border-gray-200" data-testid="interior-feature-upgrades-section">
      <div className="container-br grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="br-eyebrow text-br-bronze flex items-center gap-2">
            <Sparkles size={12} /> Interior Feature Upgrades
          </div>
          <h2 className="br-headline text-3xl sm:text-4xl mt-3">High-Impact Upgrades — Without a Full Remodel</h2>
          <p className="mt-4 text-gray-700 leading-relaxed">
            Not every home upgrade needs to be a full remodel. Black Ridge Construction helps Austin-area homeowners create high-impact interior features such as accent walls, electric fireplace walls, TV centers, media walls, built-ins, and custom trim details. These projects are a strong fit for living rooms, bedrooms, offices, and entertainment spaces where homeowners want a cleaner, more finished look.
          </p>
          <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-gray-800">
            {INTERIOR_FEATURE_BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <Check size={14} className="text-br-bronze mt-0.5 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <Link
              to="/services/interior-feature-upgrades"
              className="br-btn-primary"
              data-testid="interior-feature-cta"
            >
              Request an Interior Feature Upgrade Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <Link to="/services/interior-feature-upgrades" className="block group">
            <div className="relative aspect-[4/3] rounded overflow-hidden border border-gray-200">
              <img
                src="https://images.unsplash.com/photo-1618219740975-d40978bb7378?w=1200&q=80"
                alt="Custom interior feature wall with built-in fireplace and TV center"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-br-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-3 left-3 right-3 text-white">
                <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze-2">View Project Type</div>
                <div className="font-display text-lg tracking-widest uppercase">Interior Feature Upgrades</div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function PopularAustinServices() {
  return (
    <>
      <SEO title="Popular Austin Home Improvement Services | Black Ridge Construction" description="The Austin-area home improvement projects in highest demand right now — outdoor living, interior feature upgrades, ADUs, kitchen and bath upgrades, storm-resistant exterior, and more." />

      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Trending in Austin</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Practical Improvements Austin Homeowners Are Investing In</h1>
            <p className="mt-4 text-gray-300">Austin homes are changing, and Black Ridge Construction helps homeowners keep up with practical, valuable improvements — the upgrades that hold their value and actually make the home better to live in.</p>
          </div>
        </div>
      </section>

      <InteriorFeatureUpgradesSection />

      <section className="section-pad bg-br-paper">
        <div className="container-br space-y-12">
          {SECTIONS.map((sec) => (
            <div key={sec.title}>
              <h2 className="br-headline text-2xl sm:text-3xl mb-4">{sec.title}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {sec.items.map(([label, to]) => (
                  <Link key={label} to={to} className="group p-5 bg-white border border-gray-200 rounded hover:border-br-bronze hover:shadow-md transition flex items-center justify-between gap-3">
                    <span className="font-display tracking-widest uppercase text-sm">{label}</span>
                    <ArrowRight size={16} className="text-br-bronze group-hover:translate-x-1 transition" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
